import { NavLink } from 'react-router-dom';
import { useUiStore } from '../store/uiStore';
import { ChevronLeft, ChevronRight, LayoutDashboard } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

export interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

interface Props {
  /** Project name shown in sidebar header (e.g. "issues", "rc sim"). */
  brand?: string;
  /** Brand line above the name — defaults to "arkturian". */
  brandPrefix?: string;
  /** Footer line — defaults to "{brand} · v0.1". */
  footer?: ReactNode;
  /** Navigation entries — provide via project. Defaults to a single home link. */
  navItems?: NavItem[];
}

const DEFAULT_NAV: NavItem[] = [
  { path: '/', label: 'Home', icon: LayoutDashboard, end: true },
];

export function Sidebar({
  brand = 'template',
  brandPrefix = 'arkturian',
  footer,
  navItems = DEFAULT_NAV,
}: Props) {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);

  return (
    <aside
      className="flex flex-col transition-all duration-200"
      style={{
        minWidth: collapsed ? 'var(--sidebar-collapsed-width)' : 'var(--sidebar-width)',
        width: collapsed ? 'var(--sidebar-collapsed-width)' : 'var(--sidebar-width)',
        background: 'var(--white)',
        borderRight: '1px solid var(--border)',
        flexShrink: 0,
      }}
    >
      {/* Header */}
      <div
        className={`flex items-center h-16 ${collapsed ? 'px-3 justify-center' : 'px-6'} border-b border-[var(--border)]`}
      >
        {!collapsed && (
          <div className="flex flex-col leading-tight overflow-hidden">
            <span className="text-[11px] tracking-[0.15em] text-[var(--text-muted)] lowercase">
              {brandPrefix}
            </span>
            <span className="text-[15px] font-normal tracking-[0.15em] text-[var(--text-primary)]">
              {brand}
            </span>
          </div>
        )}
        <button
          onClick={toggleSidebar}
          className={`${collapsed ? '' : 'ml-auto'} p-1.5 rounded hover:bg-[var(--bg-light)] text-[var(--text-muted)] transition-colors`}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className={`flex-1 py-3 ${collapsed ? 'px-2' : 'px-3'}`}>
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.end}
            title={collapsed ? item.label : undefined}
            className={({ isActive }) =>
              `flex items-center gap-3 ${collapsed ? 'justify-center px-0' : 'px-3'} py-2.5 transition-colors text-[14px] tracking-[0.01em] ${
                isActive
                  ? 'text-[var(--text-primary)] font-medium'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
              }`
            }
          >
            <item.icon size={16} className="flex-shrink-0" strokeWidth={1.5} />
            {!collapsed && <span>{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      {!collapsed && (
        <div className="px-6 py-4 border-t border-[var(--border)] text-[11px] tracking-[0.15em] text-[var(--text-muted)] lowercase">
          {footer ?? `${brand} · v0.1`}
        </div>
      )}
    </aside>
  );
}
